import { useEffect, useState } from "react";
import { FaGift } from "react-icons/fa";
import { useAuthContext } from "../../context/auth";
import { getCumpleanos, universoRolesPara, formatDiaMes } from "../../utils/cumpleanos";
import styles from "./stylesCumpleanos.module.scss";

// Saludo de cumpleaños del Home (/admin). Vive DENTRO del <Header> vía la prop `centerContent`
// (no es una franja/tarjeta en el cuerpo). En pantalla es SOLO un contador ("N cumpleaños esta
// semana"), una línea de texto sutil, chica, en escala de grises (ícono monocromo `FaGift` + texto,
// mismo gris); el detalle completo (nombre, entidad, día) va en el `title`. Se muestra SIEMPRE para
// el universo que corresponde (decisión de Ignacio: que el equipo entienda que hay que cargar el
// dato), tenga o no cumpleaños. Toda la lógica de universo/país/ventana vive en
// src/utils/cumpleanos.js (fuente única); acá solo presentación.

const PAIS_LABEL = { CHI: "Chile", PER: "Perú" };

export const CumpleanosBanner = () => {
  const { pais, rol } = useAuthContext();
  const [data, setData] = useState({ hoy: [], proximos: [], error: false });
  const [loading, setLoading] = useState(true);

  const roles = universoRolesPara(rol);

  useEffect(() => {
    if (!roles || !pais) return undefined; // sin universo o sin país aún → no se consulta
    let vivo = true;
    setLoading(true);
    getCumpleanos({ rolViewer: rol, paisViewer: pais }).then((res) => {
      if (!vivo) return;
      setData(res);
      setLoading(false);
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rol, pais]);

  // Rol fuera de la matriz cerrada → el bloque NO se renderiza. Los hooks ya corrieron arriba,
  // así que este return condicional no rompe las reglas de hooks.
  if (!roles) return null;

  const { hoy, proximos } = data;
  const count = hoy.length + proximos.length;
  // Region ve Chile + Perú juntos → se indica el país en cada línea del detalle (title).
  const mostrarPais = pais === "Region";
  const tagPais = (p) => (mostrarPais && PAIS_LABEL[p] ? ` (${PAIS_LABEL[p]})` : "");

  const fmtHoy = (f) => `${f.nombre}${f.entidad ? `, de ${f.entidad}` : ""}${tagPais(f.pais)}`;
  const fmtProx = (f) =>
    `${formatDiaMes(f.mes, f.dia)} ${f.nombre}${f.entidad ? `, ${f.entidad}` : ""}${tagPais(f.pais)}`;

  // En pantalla, SOLO el contador (sin nombres/entidades/fechas/listas). "cumpleaños" es invariable
  // en singular y plural, así que "1 cumpleaños esta semana" / "5 cumpleaños esta semana".
  // El detalle completo va en el `title` (hover), nunca en pantalla.
  let resumen;
  let detalle;
  if (loading) {
    resumen = "Cargando cumpleaños…";
    detalle = resumen;
  } else if (count === 0) {
    resumen = "Sin cumpleaños esta semana";
    detalle = resumen;
  } else {
    resumen = `${count} cumpleaños esta semana`;
    const segmentos = [];
    if (hoy.length > 0) {
      segmentos.push(`${hoy.length > 1 ? "Hoy cumplen" : "Hoy cumple"} ${hoy.map(fmtHoy).join("; ")}`);
    }
    if (proximos.length > 0) {
      segmentos.push(`Próximos: ${proximos.map(fmtProx).join("; ")}`);
    }
    detalle = segmentos.join(" · ");
  }

  return (
    <span className={styles.linea} title={detalle}>
      <FaGift className={styles.icono} aria-label="cumpleaños" />
      {resumen}
    </span>
  );
};
